// Undo: every write is an op in the local log with a snapshot of what it
// touched, so the last one can be put back. Undo never rewrites history; it
// makes new ops, the way the capture put-back does, and marks them so a second
// press walks further back instead of redoing what it just undid.
import { db as runtimeDb, mutate as runtimeMutate } from '@/runtime'
import type { Db } from '@/db/schema'
import type { Mutator } from '@/db/ops'
import type { ListItem } from '@/kinds/list/schema'
import type { OpRecord } from '@/db/types'
import { useUi } from '@/state/ui'

/** Ops a single user action made, newest group first, ready to undo. */
const groupOf = (recs: OpRecord[], rec: OpRecord): OpRecord[] =>
  rec.group ? recs.filter((r) => r.group === rec.group) : [rec]

/**
 * The most recent user action that can still be taken back: not already
 * undone, not itself an undo, not the target of one, and reversible.
 */
export const lastUndoable = async (
  db: Db = runtimeDb,
  mutate: Mutator = runtimeMutate,
): Promise<{ recs: OpRecord[]; summary: string } | null> => {
  const recs = await db.ops.orderBy('created_at').reverse().limit(200).toArray()
  const undone = new Set<string>()
  for (const r of recs) {
    if (r.undo_of) undone.add(r.undo_of)
    if (r.undone_at) undone.add(r.op_id)
  }
  for (const rec of recs) {
    if (rec.undo_of || rec.no_undo || undone.has(rec.op_id) || rec.state === 'rejected') continue
    // a group can be longer than the window (the course seed is hundreds of ops): take all of it
    const whole = rec.group ? await db.ops.filter((r) => r.group === rec.group).toArray() : [rec]
    for (const r of whole) if (r.undo_of) undone.add(r.undo_of)
    const group = (whole.length > groupOf(recs, rec).length ? whole : groupOf(recs, rec)).filter(
      (r) => !r.no_undo,
    )
    if (!group.length) continue // a receipt recording itself is not a user action
    if (group.some((r) => undone.has(r.op_id))) continue
    // the newest real action is the only candidate: reaching past something that
    // cannot be undone to undo an older, unrelated change would be a nasty surprise
    if (!group.every((r) => reversible(r, mutate))) return null
    return { recs: group, summary: group[group.length - 1]!.summary }
  }
  return null
}

/** Whether this op has a known inverse. */
const reversible = (rec: OpRecord, mutate: Mutator): boolean => {
  switch (rec.type) {
    case 'create':
    case 'delete':
    case 'restore':
    case 'edge':
      return true
    case 'patch':
      return !!rec.before
    case 'action':
      return !!rec.before && !!inverseAction(rec, mutate)
    default:
      return false
  }
}

type Step = () => Promise<unknown>

/** An exercise's answer goes back to what it was: open, or its earlier result. */
const resetAnswer = (id: string, status: unknown, mutate: Mutator): Step => {
  const was = status as { result?: string; answered_at?: string }
  return () =>
    mutate.action(id, 'reset', was.result ? { result: was.result, answered_at: was.answered_at } : {})
}

/** What it takes to put one op back. */
const inverseAction = (rec: OpRecord, mutate: Mutator): Step | null => {
  if (rec.type !== 'action' || !rec.before) return null
  const id = rec.id
  const before = rec.before
  const beforeSpec = before.spec as { items?: ListItem[]; scheduled?: string }
  const beforeState = (before.status as { state?: string }).state
  const args = (rec.args ?? {}) as Record<string, unknown>
  switch (rec.action) {
    // list items: only ever through actions, so they merge across devices
    case 'tick':
      return () => mutate.action(id, 'untick', { id: args.id })
    case 'untick':
      return () => mutate.action(id, 'tick', { id: args.id })
    case 'add_items':
      return async () => {
        for (const it of (args.items ?? []) as ListItem[])
          await mutate.action(id, 'remove_item', { id: it.id })
      }
    case 'remove_item': {
      const gone = beforeSpec.items?.find((i) => i.id === args.id)
      if (!gone) return null
      return () => mutate.action(id, 'add_items', { items: [gone] })
    }
    case 'edit_item': {
      const was = beforeSpec.items?.find((i) => i.id === args.id)
      if (!was) return null
      return () => mutate.action(id, 'edit_item', { id: was.id, text: was.text })
    }
    case 'move_item': {
      const items = beforeSpec.items ?? []
      const i = items.findIndex((x) => x.id === args.id)
      if (i < 0) return null
      const after = items[i + 1]
      return () => mutate.action(id, 'move_item', { id: args.id, before: after?.id })
    }
    case 'clear_ticked': {
      const gone = (beforeSpec.items ?? []).filter((i) => i.done)
      if (!gone.length) return null
      return async () => {
        await mutate.action(id, 'add_items', { items: gone.map((i) => ({ ...i, done: false })) })
        for (const i of gone) await mutate.action(id, 'tick', { id: i.id })
      }
    }
    case 'reset': {
      if (rec.kind === 'Exercise') return resetAnswer(id, before.status, mutate) // an answer put back, undone
      const ticked = (beforeSpec.items ?? []).filter((i) => i.done)
      return async () => {
        for (const i of ticked) await mutate.action(id, 'tick', { id: i.id })
      }
    }
    // task and project state: back through the named actions, never by patching status
    case 'complete':
    case 'drop':
      return async () => {
        await mutate.action(id, 'reopen')
        if (beforeState && beforeState !== 'next') await mutate.action(id, 'move', { state: beforeState })
      }
    case 'reopen':
      if (rec.kind === 'Lesson') {
        if (beforeState === 'done') return () => mutate.action(id, 'mark_done')
        if (beforeState === 'skipped') return () => mutate.action(id, 'skip')
        return null
      }
      if (beforeState === 'done') return () => mutate.action(id, 'complete')
      if (beforeState === 'dropped') return () => mutate.action(id, 'drop')
      return null
    case 'move':
      if (!beforeState) return null
      return () => mutate.action(id, 'move', { state: beforeState })
    case 'set_familiarity': {
      // a concept's familiarity goes back to what it was
      const was = (before.status as { familiarity?: string }).familiarity
      if (!was) return null
      return () => mutate.action(id, 'set_familiarity', { state: was })
    }
    // a lesson's state goes back through its own actions
    case 'mark_done':
    case 'skip':
      return () => mutate.action(id, 'reopen')
    // a learn session: a card taken back, and the end undone (it reopens the same sitting)
    case 'item_done':
      return () => mutate.action(id, 'item_undone', args.result ? { result: args.result } : undefined)
    case 'item_undone':
      return () => mutate.action(id, 'item_done', args.result ? { result: args.result } : undefined)
    // an exercise's answer goes back to what it was (open, or its earlier result)
    case 'answer':
      return resetAnswer(id, before.status, mutate)
    case 'end':
      return () => mutate.action(id, 'resume')
    case 'resume': {
      const mins = (before.status as { actual_minutes?: number }).actual_minutes
      return () => mutate.action(id, 'end', mins === undefined ? undefined : { actual_minutes: mins })
    }

    case 'start':
      return () => mutate.action(id, 'stop')
    case 'stop':
      return () => mutate.action(id, 'start')
    case 'defer':
      if (!beforeSpec.scheduled) return null
      return () => mutate.action(id, 'defer', { scheduled: beforeSpec.scheduled })
    case 'set_item_reminder':
      return () => mutate.action(id, 'set_item_reminder', { id: args.id, remind_at: null })
    case 'touch':
      return async () => {}
    case 'done_now': {
      // the day it was last done goes back ("" for never); stale_reminded_for is left alone.
      // A date the forward action cleared comes back as an ordinary patch.
      if (args.day !== undefined) return null
      const was = (before.status as { done_at?: string }).done_at ?? ''
      const bs = before.spec as { scheduled?: string; scheduled_time?: string }
      return async () => {
        await mutate.action(id, 'done_now', { day: was })
        if (bs.scheduled)
          await mutate.patch(id, {
            spec: {
              scheduled: bs.scheduled,
              ...(bs.scheduled_time ? { scheduled_time: bs.scheduled_time } : {}),
            },
          })
      }
    }
    case 'link_task':
      // an agent session tied to a task: tie it back to what it had
      return () =>
        mutate.action(id, 'link_task', { task_id: (before.status as { task_id?: string }).task_id ?? '' })
    case 'close_artifact': {
      // a page taken from beside Prime's thread comes back (at the front)
      const was = ((before.status as { artifacts?: { id: string; seen?: string }[] }).artifacts ?? []).find(
        (x) => x.id === args.page_id,
      )
      if (!was) return null
      return () =>
        mutate.action(id, 'show_artifact', { page_id: was.id, ...(was.seen ? { seen: was.seen } : {}) })
    }
    default:
      return null
  }
}

const stepFor = (rec: OpRecord, mutate: Mutator): Step | null => {
  switch (rec.type) {
    case 'create':
      return () => mutate.remove(rec.resource.id)
    case 'delete':
      return () => mutate.restore(rec.id)
    case 'restore':
      return () => mutate.remove(rec.id)
    case 'edge':
      return () => mutate.setEdge(rec.edge.from_id, rec.edge.to_id, rec.edge.relation, !rec.deleted)
    case 'patch': {
      if (!rec.before) return null
      const spec: Record<string, unknown | null> = {}
      for (const key of Object.keys(rec.spec ?? {})) spec[key] = rec.before.spec[key] ?? null
      const labels: Record<string, string | null> = {}
      for (const key of Object.keys(rec.labels ?? {})) labels[key] = rec.before.labels[key] ?? null
      return () =>
        mutate.patch(rec.id, {
          ...(Object.keys(spec).length ? { spec } : {}),
          ...(Object.keys(labels).length ? { labels } : {}),
        })
    }
    case 'action':
      return inverseAction(rec, mutate)
    default:
      return null
  }
}

/**
 * Take back the last user action. Returns what was undone, or null when there
 * is nothing to take back.
 */
export const undoLast = async (
  db: Db = runtimeDb,
  mutate: Mutator = runtimeMutate,
): Promise<string | null> => {
  const found = await lastUndoable(db, mutate)
  if (!found) return null
  // newest op first: a group is unwound in reverse, like the capture put-back
  const ordered = [...found.recs].sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
  const anchor = ordered[0]!
  await mutate.undoing(anchor.op_id, async () => {
    for (const rec of ordered) {
      const step = stepFor(rec, mutate)
      if (step) await step()
    }
  })
  const at = new Date().toISOString()
  for (const rec of ordered) await db.ops.update(rec.op_id, { undone_at: at })
  return found.summary
}

/** Undo bound to a button or ⌘Z: does it, then says so. */
export const undoWithFlash = async () => {
  const flash = useUi.getState().showFlash
  try {
    const what = await undoLast()
    flash(what ? `Undone: ${what.toLowerCase()}` : 'Nothing to undo', 2500)
  } catch (e) {
    flash((e as Error).message || 'That could not be undone', 3000)
  }
}
